import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Upload, FileText, Download } from "lucide-react";
import { formatDate } from "@/lib/constants";

export default function DocumentsTab({ clientId, canEdit }) {
  const [docs, setDocs] = useState([]);
  const [file, setFile] = useState(null);
  const [type, setType] = useState("Other");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/clients/${clientId}/documents`);
    setDocs(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId]);

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("doc_type", type);
    try {
      await api.post(`/clients/${clientId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      setFile(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); } finally { setBusy(false); }
  };

  const download = async (d) => {
    try {
      const res = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: d.content_type }));
      const a = document.createElement("a");
      a.href = url; a.download = d.original_filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  return (
    <div className="space-y-6">
      {canEdit && (
        <Card className="border-border bg-white p-5">
          <h3 className="mb-3 font-dashboard text-base font-bold">Upload document</h3>
          <div className="grid gap-3 md:grid-cols-[1fr_180px_120px]">
            <Input type="file" onChange={(e) => setFile(e.target.files?.[0])} data-testid="doc-file-input" />
            <select className="rounded-md border border-input bg-background px-3 text-sm"
              value={type} onChange={(e) => setType(e.target.value)} data-testid="doc-type-select">
              <option>PAN</option><option>Aadhaar</option><option>Policy</option><option>Other</option>
            </select>
            <Button onClick={upload} disabled={!file || busy} className="gap-2" data-testid="doc-upload-btn">
              <Upload className="h-4 w-4" /> {busy ? "Uploading…" : "Upload"}
            </Button>
          </div>
        </Card>
      )}
      <div className="space-y-2">
        {docs.length === 0 && <p className="text-sm text-muted-foreground">No documents.</p>}
        {docs.map((d) => (
          <div key={d.id} className="flex items-center justify-between rounded-md border border-border bg-white p-4">
            <div className="flex items-center gap-3">
              <FileText className="h-4 w-4 text-muted-foreground" />
              <div>
                <div className="font-medium">{d.original_filename}</div>
                <div className="text-xs text-muted-foreground">{d.type} · {formatDate(d.uploaded_at)}</div>
              </div>
            </div>
            <Button size="sm" variant="outline" onClick={() => download(d)} className="gap-1" data-testid={`doc-download-${d.id}`}>
              <Download className="h-3 w-3" /> Download
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
